<template>
  <FullPage
    class="tasks personal-tasks"
    with-find
    @contextmenu.prevent
    @find="openFindBar"
  >
    <template #topbar-left>
      <Breadcrumb :items="breadcrumbItems" />
    </template>
    <FindBar
      v-if="isFindBarOpen"
      ref="findBarEl"
      container-id="scrollable-tasks"
      @close="closeFindBar"
    />
    <BaseNumberedHeader
      v-if="!isTitleScrolledPast"
      title="Tasks"
      :number="length"
    />
    <BaseTabs
      v-model:selected-tab="selectedTab"
      :tabs-titles="tabsTitles"
      class="tabs"
      storage-key="tasks:tab"
    />
    <!-- The lists are normally paginated; while the find bar is open they
         render in full so the search covers every task, not just the loaded
         window. -->
    <component
      :is="displayedTaskList"
      id="scrollable-tasks"
      ref="listEl"
      v-model:length="length"
      class="taskslist"
      :show-all="isFindBarOpen"
      :task-scope="taskScope"
    />
  </FullPage>
</template>

<script lang="ts">
import { match } from "ts-pattern";
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  ref,
  watch,
  type Component,
} from "vue";

import BaseNumberedHeader from "@/components/base/BaseNumberedHeader.vue";
import BaseTabs from "@/components/base/BaseTabs.vue";
import Breadcrumb, { type BreadcrumbItem } from "@/components/Breadcrumb.vue";
import FindBar from "@/components/find/FindBar.vue";
import { useFindBar } from "@/components/find/useFindBar";
import * as inject from "@/components/inject";
import Recurrences from "@/components/tasks/Recurrences.vue";
import { usePersonalTaskScope } from "@/components/tasks/taskScope";
import Unplanned from "@/components/tasks/Unplanned.vue";
import Upcoming from "@/components/tasks/Upcoming.vue";
import FullPage from "@/components/templates/FullPage.vue";
import { getHTMLElement } from "@/components/utils";

type TasksTab = "Unplanned" | "Recurrences" | "Upcoming";

export default defineComponent({
  name: "PersonalTasks",
  components: {
    BaseNumberedHeader,
    BaseTabs,
    Breadcrumb,
    FindBar,
    FullPage,
  },
  setup() {
    const controller = inject.controller();
    const profile = inject.profile();
    const taskScope = usePersonalTaskScope(controller, profile);

    const { isFindBarOpen, findBarEl, openFindBar, closeFindBar } =
      useFindBar();

    const tabsTitles: TasksTab[] = ["Unplanned", "Recurrences", "Upcoming"];
    const selectedTab = ref<TasksTab>(tabsTitles[0]);
    const length = ref(0);
    watch(selectedTab, () => (length.value = 0));

    const listEl = ref<
      | InstanceType<typeof Unplanned>
      | InstanceType<typeof Recurrences>
      | InstanceType<typeof Upcoming>
    >();
    const displayedTaskList = computed<Component>(() =>
      match(selectedTab.value)
        .with("Unplanned", () => Unplanned)
        .with("Recurrences", () => Recurrences)
        .with("Upcoming", () => Upcoming)
        .exhaustive()
    );

    const isTitleScrolledPast = ref(false);
    let scrollCleanup: (() => void) | null = null;

    watch(
      listEl,
      (newEl) => {
        scrollCleanup?.();
        scrollCleanup = null;
        isTitleScrolledPast.value = false;
        const el = newEl ? getHTMLElement(newEl) : undefined;
        if (!el) return;

        const handler = () => {
          isTitleScrolledPast.value = el.scrollTop > 0;
        };
        el.addEventListener("scroll", handler);
        scrollCleanup = () => el.removeEventListener("scroll", handler);
      },
      { flush: "post" }
    );

    onBeforeUnmount(() => {
      scrollCleanup?.();
    });

    const breadcrumbItems = computed((): BreadcrumbItem[] => {
      const item: BreadcrumbItem = {
        title: "Tasks",
        link: { params: { id: "tasks" } },
        fadeIn: true,
        onClick: () => {
          const el = listEl.value ? getHTMLElement(listEl.value) : undefined;
          el?.scrollTo({ top: 0, behavior: "smooth" });
        },
      };
      return isTitleScrolledPast.value ? [item] : [];
    });

    return {
      breadcrumbItems,
      closeFindBar,
      displayedTaskList,
      findBarEl,
      isFindBarOpen,
      isTitleScrolledPast,
      length,
      listEl,
      openFindBar,
      selectedTab,
      tabsTitles,
      taskScope,
    };
  },
});
</script>

<style scoped lang="scss">
$margin: 64px;

.tasks {
  .tabs,
  .base-numbered-header {
    padding: 0 $margin;
    width: calc(100% - $margin * 2);
  }
  .tabs {
    margin-inline-start: -4px;
  }
  :deep(.content) {
    width: 100%;
    padding-inline-start: 0;
    padding-inline-end: 0;
    // Anchor for the find bar, which must not scroll with the list.
    position: relative;
  }
  .taskslist {
    display: flex;
    flex-direction: column;
    padding: 0 $margin;
    max-width: calc(100% - $margin * 2);
  }

  .taskslist:not(.empty) {
    overflow-y: scroll;
    overflow: auto;
  }

  .taskslist:not(.upcoming-task-list) {
    margin-inline-start: -12px;
  }
}
</style>
